<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import { useAuthForm } from '@/composables/useAuthForm'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth-store'

const router = useRouter()
const authStore = useAuthStore()

const {
  email,
  password,
  errors,
  handleSubmit,
  loading,
  showToast,
} = useAuthForm('login')

const handleLogin = handleSubmit(async (values) => {
  loading.value = true
  try {
    const result = await authStore.loginUser({
      email: email.value,
      password: password.value,
    })

    if(result){
      showToast('success', 'Login Successful', 'You are now logged in.')
      await router.push('/dashboard')
    }

  } catch (error: any) {
    if (error.response?.data?.errors) {
      const backendErrors = error.response.data.errors
      const errorMessage = backendErrors
        .map((err: { code: string; description: string }) => err.description)
        .join('\n')
      showToast('error', 'Login Failed', errorMessage)
    } else if (error.response) {
      showToast(
        'error',
        'Login Failed',
        `Error ${error.response.status}: ${error.response.data.title || error.message}`
      )
    } else {
      showToast('error', 'Login Failed', error.message || 'An unexpected error occurred.')
    }
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="flex h-screen">
    <div class="hidden md:flex w-1/2 bg-[#f8f8f8] items-center justify-center bg-image"></div>

    <div class="w-full md:w-1/2 flex flex-col justify-center items-center px-6 sm:px-10">
      <img src="@/assets/images/logo.png" alt="Techknital Logo" class="w-48 mb-12" />

      <div class="text-center mb-12">
        <h2 class="text-3xl font-semibold text-gray-800 mb-2">Welcome Home</h2>
        <p class="text-gray-500">Welcome back! Please, enter your details.</p>
      </div>

      <form @submit.prevent="handleLogin" class="w-full max-w-sm space-y-8">
        <div>
          <label for="email" class="block text-gray-700 font-medium mb-2">Email</label>
          <InputText
            id="email"
            v-model="email"
            placeholder="Enter your Email"
            class="w-full"
            :class="{ 'p-invalid': errors.email }"
          />
          <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
        </div>

        <div>
          <label for="password" class="block text-gray-700 font-medium mb-2">Password</label>
          <Password
            id="password"
            v-model="password"
            :feedback="false"
            placeholder="Enter your password"
            class="w-full"
            :class="{ 'p-invalid': errors.password }"
          />
          <small v-if="errors.password" class="text-red-500">{{ errors.password }}</small>
        </div>

        <div class="text-right -mt-2 mb-2">
          <RouterLink to="/forgot-password" class="text-sm text-gray-600 underline">Forgot password?</RouterLink>
        </div>

        <Button type="submit" label="Log in" class="w-full bg-primary-500 border-none text-white text-lg py-3 rounded" :loading="loading" />
      </form>
    </div>
  </div>
</template>
<style scoped>
.p-password input {
  width: 100%;
}
.bg-image{
  background-image: url('@/assets/images/login_image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f8f8;
}
</style>